/**
 * Bootstrap — attend explicitement le chargement de la langue détectée avant
 * de monter React. Évite un flash de fallback FR sur les autres langues.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import { initSentry } from "./lib/sentry";
initSentry();
import { initPostHog } from "./lib/posthog";
initPostHog();

// Auto-update service worker: when a new version is deployed, refresh
// immediately so users always run the latest code. Polls every 60s while
// the app is open, in case the user keeps it open for hours.
registerSW({
  immediate: true,
  onNeedRefresh() {
    // skipWaiting + clientsClaim are set in vite.config — just reload.
    window.location.reload();
  },
  onRegisteredSW(_swUrl: string, reg: ServiceWorkerRegistration | undefined) {
    if (!reg) return;
    setInterval(() => {
      reg.update().catch(() => {});
    }, 60_000);
  },
});

import App from './App';
import './styles/index.css';
import i18n, { ensureLangLoaded, SUPPORTED_LANGS, type LangCode } from './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

// Source unique : SUPPORTED_LANGS (i18n/index.ts) — FR/EN/NL depuis le Lot 1.
const SUPPORTED: LangCode[] = SUPPORTED_LANGS.map((l) => l.code);

async function bootstrap(): Promise<void> {
  const stored = (localStorage.getItem('nia-lang') ?? '').toLowerCase() as LangCode;
  const browser = (navigator.language ?? 'fr').slice(0, 2).toLowerCase() as LangCode;
  const initial = SUPPORTED.includes(stored)
    ? stored
    : SUPPORTED.includes(browser)
      ? browser
      : 'fr';

  if (initial !== 'fr') {
    try {
      await ensureLangLoaded(initial);
      await i18n.changeLanguage(initial);
    } catch (e) {
      console.warn('[i18n] failed to load', initial, e);
    }
  }

  createRoot(rootEl as HTMLElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
